/**
 * The City Satisfaction settings GUS would silently ignore, found before Save.
 *
 * GUS reads `features.modules.atlas.tabs.satisfaction` and swaps any value it
 * cannot use for its own default without a word, so a bad value has to be
 * stopped by the portal. The backend's check
 * (backend/app/services/satisfaction_settings.py) is what refuses the write;
 * this copy lets the editor say what is wrong while the admin is still typing.
 * Both run against one table of cases (satisfactionSettings.cases.json), rule
 * id for rule id, so they cannot drift apart.
 *
 * Absent and null mean "use GUS's default" for every setting here, so neither
 * is ever refused. Text is judged as GUS's Python judges it: stripped of the
 * characters Python's str.strip() removes (not JavaScript's trim()), and
 * measured in characters (code points), not UTF-16 units.
 */
import lexicon from './satisfactionLexicon.json';

export const SATISFACTION_PATH = 'modules.atlas.tabs.satisfaction';

export interface Bilingual {
  en: string;
  ar: string;
}

export interface ReasonOption extends Bilingual {
  id: string;
}

/** What a place can be "loved for": GUS's praise lexicon. */
export const PRAISE_REASONS: readonly ReasonOption[] = lexicon.praise;
/** What people raise: GUS's reason lexicon, for place_quality_raised. */
export const RAISED_REASONS: readonly ReasonOption[] = lexicon.raised;
export const DEFAULT_LOVED: readonly string[] = lexicon.loved_default;
export const DEFAULT_LOVED_GROUPS: Readonly<Record<LovedGroup, readonly string[]>> =
  lexicon.loved_groups_default as Record<LovedGroup, string[]>;
export const CENSUS_FIELDS: readonly string[] = lexicon.census_fields;
export const LOVED_GROUP_IDS = ['offers', 'look_and_feel'] as const;
export type LovedGroup = typeof LOVED_GROUP_IDS[number];
export const DIALECT_SIDES = ['national', 'reference'] as const;
export const GAP_LANGUAGES = ['en', 'ar'] as const;
export const GAP_MAX_ITEMS = 12;
export const GAP_MAX_CHARS = 80;
/** GUS settings.py `_COLUMN`, verbatim. */
export const COLUMN_NAME = /^[a-z][a-z0-9_]{0,62}$/;
/** GUS SAT_THRESHOLD_DEFAULTS MIN_N: the map's own first Grey below choice; it offers only values above it. */
export const MIN_N_DEFAULT = 30;
/** GUS settings.py `_ICON_KEY` (Python's `.` is "anything but a newline") and `_ICON_NAME`, and the name's cap. */
export const ICON_KEY = /^(\*|[TSDG]:[^\n]+)$/;
export const ICON_NAME = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const ICON_MAX_CHARS = 40;
/** What topic_icons may hold: GUS reads `icons` and `fallback`; `enabled` is the manifest's own switch for it. */
export const TOPIC_ICONS_FIELDS: readonly string[] = ['icons', 'fallback', 'enabled'];
/** GUS flags.py SECTIONS: the tab's switches that sit directly on the tab node. */
export const SWITCH_SECTIONS: readonly string[] = [
  'chart', 'compare', 'stands_out', 'source_picker', 'cohorts', 'facilities', 'quotes', 'ai_summary',
  'pulse', 'headline_news', 'topic_rail', 'compare_maps', 'where_to_act', 'learn_from', 'why', 'changes',
  'watchlist', 'who', 'loved', 'brief', 'brief_ai',
];
/** The groups whose every child is a switch (`subtabs.trends`, `detectors.gap`, `rules.mentor`). */
export const SWITCH_GROUPS: readonly string[] = ['subtabs', 'detectors', 'rules'];

const PRAISE_IDS = new Set(PRAISE_REASONS.map((r) => r.id));
const RAISED_IDS = new Set(RAISED_REASONS.map((r) => r.id));

export interface SettingError {
  /** Full dot path from the root of feature_flags. */
  path: string;
  /** Stable rule id, shared with the backend and the cases file. */
  rule: string;
  message: Bilingual;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const isWhole = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
const isStringList = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
const dupes = (items: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of items) {
    if (seen.has(x) && !out.includes(x)) out.push(x);
    seen.add(x);
  }
  return out;
};
const list = (xs: string[]) => xs.join(', ');

/** The characters Python's str.isspace() accepts, which is what GUS's .strip() removes. */
const PY_SPACE = '\t\n\v\f\r\x1c-\x1f \x85\xa0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000';
const PY_STRIP = new RegExp(`^[${PY_SPACE}]+|[${PY_SPACE}]+$`, 'g');
/** Python's str.strip(). */
export const pyStrip = (s: string): string => s.replace(PY_STRIP, '');
/** Python's len() on a str: characters, not UTF-16 units. */
export const charCount = (s: string): number => Array.from(s).length;

/** The map's minimum post count, as GUS's load_thresholds reads `config.thresholds.MIN_N`. */
export function effectiveMinN(cfg: unknown): number {
  const thr = isPlainObject(cfg) ? cfg.thresholds : undefined;
  const v = isPlainObject(thr) ? thr.MIN_N : undefined;
  return typeof v === 'number' && !Number.isNaN(v) ? v : MIN_N_DEFAULT;
}

const iconNameOk = (v: unknown): boolean =>
  typeof v === 'string' && charCount(v) <= ICON_MAX_CHARS && ICON_NAME.test(v);

function switchErrors(node: unknown, path: string, out: SettingError[]): void {
  if (!isPlainObject(node)) return;
  const groupPaths = SWITCH_GROUPS.map((g) => `${SATISFACTION_PATH}.${g}`);
  for (const [key, value] of Object.entries(node)) {
    const here = `${path}.${key}`;
    if (key === 'config' && path === SATISFACTION_PATH) continue;
    const group = path === SATISFACTION_PATH && SWITCH_GROUPS.includes(key);
    const isSwitch = (path === SATISFACTION_PATH && SWITCH_SECTIONS.includes(key)) || groupPaths.includes(path);
    if ((group || isSwitch) && value !== null && value !== undefined && !isPlainObject(value)
      && !(isSwitch && typeof value === 'boolean')) {
      out.push({ path: here, rule: 'switch_shape',
        message: { en: 'A switch is on or off; GUS reads this value as on, or not at all.',
          ar: 'المفتاح إما مفعّل أو غير مفعّل؛ يقرأ GUS هذه القيمة على أنها مفعّلة، أو لا يقرؤها أصلًا.' } });
    } else if (key === 'enabled' && value !== null && value !== undefined && typeof value !== 'boolean') {
      out.push({ path: here, rule: 'switch_enabled_boolean',
        message: { en: 'A switch is on or off.', ar: 'المفتاح إما مفعّل أو غير مفعّل.' } });
    } else if (isPlainObject(value)) {
      switchErrors(value, here, out);
    }
  }
}

function configErrors(cfg: Record<string, unknown>, base: string, out: SettingError[]): void {
  const push = (key: string, rule: string, en: string, ar: string) =>
    out.push({ path: `${base}.${key}`, rule, message: { en, ar } });
  const present = (v: unknown) => v !== null && v !== undefined;

  const grey = cfg.grey_below;
  if (present(grey)) {
    if (!Array.isArray(grey)) {
      push('grey_below', 'grey_below_list', 'Grey below is a list of post counts.', '«رمادي تحت» قائمة بأعداد المنشورات.');
    } else if (!grey.every((x) => isWhole(x) && x > 0)) {
      push('grey_below', 'grey_below_whole_positive', 'Each Grey below choice is a whole number of posts above 0.',
        'كل خيار في «رمادي تحت» عدد صحيح من المنشورات أكبر من صفر.');
    } else if ((grey as number[]).some((x) => x <= effectiveMinN(cfg))) {
      const minN = effectiveMinN(cfg);
      const low = list((grey as number[]).filter((x) => x <= minN).map(String));
      push('grey_below', 'grey_below_above_floor',
        `The map always offers its minimum post count (${minN}) and only choices above it; GUS would drop ${low}.`,
        `تعرض الخريطة دائمًا الحد الأدنى لعدد المنشورات (${minN}) والخيارات الأعلى منه فقط؛ سيتجاهل GUS: ${low}.`);
    }
  }

  const loved = cfg.loved_reasons;
  let lovedOk = false;
  if (present(loved)) {
    if (!isStringList(loved)) {
      push('loved_reasons', 'loved_reasons_list', 'Loved reasons is a list of reason ids.', 'أسباب التفضيل قائمة بمعرّفات الأسباب.');
    } else if (loved.length === 0) {
      push('loved_reasons', 'loved_reasons_not_empty',
        'Choose at least one reason, or clear the setting to use the default six.',
        'اختر سببًا واحدًا على الأقل، أو امسح الإعداد لاستخدام الأسباب الستة الافتراضية.');
    } else if (loved.some((x) => !PRAISE_IDS.has(x))) {
      push('loved_reasons', 'loved_reasons_known',
        `Not a praise reason GUS knows: ${list(loved.filter((x) => !PRAISE_IDS.has(x)))}.`,
        `ليس من أسباب الإشادة المعروفة لدى GUS: ${list(loved.filter((x) => !PRAISE_IDS.has(x)))}.`);
    } else if (dupes(loved).length) {
      push('loved_reasons', 'loved_reasons_unique', `Listed twice: ${list(dupes(loved))}.`, `مذكور مرتين: ${list(dupes(loved))}.`);
    } else {
      lovedOk = true;
    }
  }
  // Groups are judged against the loved reasons GUS will actually use.
  const effectiveLoved = new Set<string>(lovedOk ? (loved as string[]) : DEFAULT_LOVED);

  const groups = cfg.loved_reason_groups;
  if (present(groups)) {
    if (!isPlainObject(groups)) {
      push('loved_reason_groups', 'loved_groups_object', 'Loved reason groups has two groups: offers, and look and feel.',
        'لمجموعات أسباب التفضيل مجموعتان: ما يقدّمه المكان، ومظهره وأجواؤه.');
    } else if (Object.keys(groups).some((k) => !(LOVED_GROUP_IDS as readonly string[]).includes(k))) {
      const extra = Object.keys(groups).filter((k) => !(LOVED_GROUP_IDS as readonly string[]).includes(k));
      push('loved_reason_groups', 'loved_groups_keys', `Only offers and look_and_feel are groups; not ${list(extra)}.`,
        `المجموعتان الوحيدتان هما offers و look_and_feel، وليس ${list(extra)}.`);
    } else if (!Object.values(groups).every(isStringList)) {
      push('loved_reason_groups', 'loved_groups_lists', 'Each group is a list of reason ids.', 'كل مجموعة قائمة بمعرّفات الأسباب.');
    } else {
      const named = (Object.values(groups) as string[][]).flat();
      const outside = Array.from(new Set(named)).filter((r) => !effectiveLoved.has(r));
      if (outside.length) {
        push('loved_reason_groups', 'loved_groups_in_loved', `Not one of this tenant's loved reasons: ${list(outside)}.`,
          `ليس من أسباب التفضيل لهذا المستأجر: ${list(outside)}.`);
      } else if (dupes(named).length) {
        push('loved_reason_groups', 'loved_groups_once', `A reason belongs to one group only: ${list(dupes(named))}.`,
          `ينتمي السبب إلى مجموعة واحدة فقط: ${list(dupes(named))}.`);
      } else if (named.length === 0) {
        push('loved_reason_groups', 'loved_groups_not_empty',
          'Put at least one reason in a group, or clear the setting to use the default.',
          'ضع سببًا واحدًا على الأقل في مجموعة، أو امسح الإعداد لاستخدام التقسيم الافتراضي.');
      }
    }
  }

  const columns = cfg.census_columns;
  if (present(columns)) {
    if (!isPlainObject(columns)) {
      push('census_columns', 'census_columns_object', 'Census columns maps a census field to a column name.',
        'أعمدة التعداد تربط كل حقل من حقول التعداد باسم عمود.');
    } else if (Object.keys(columns).some((k) => !CENSUS_FIELDS.includes(k))) {
      const extra = Object.keys(columns).filter((k) => !CENSUS_FIELDS.includes(k));
      push('census_columns', 'census_columns_field', `Not a census field GUS reads: ${list(extra)}.`,
        `ليس من حقول التعداد التي يقرؤها GUS: ${list(extra)}.`);
    } else {
      const bad = Object.entries(columns).filter(([, c]) => typeof c !== 'string' || !COLUMN_NAME.test(c)).map(([k]) => k);
      if (bad.length) {
        push('census_columns', 'census_columns_name',
          `Column names are lower-case letters, digits and underscores, starting with a letter, at most 63 characters: ${list(bad)}.`,
          `أسماء الأعمدة حروف لاتينية صغيرة وأرقام وشرطات سفلية، تبدأ بحرف، ولا تتجاوز 63 حرفًا: ${list(bad)}.`);
      }
    }
  }

  const dialects = cfg.dialects;
  if (present(dialects)) {
    if (!isPlainObject(dialects)) {
      push('dialects', 'dialects_object', 'Dialects names a national and a reference dialect.', 'اللهجات: لهجة وطنية ولهجة مرجعية.');
    } else if (Object.keys(dialects).some((k) => !(DIALECT_SIDES as readonly string[]).includes(k))) {
      const extra = Object.keys(dialects).filter((k) => !(DIALECT_SIDES as readonly string[]).includes(k));
      push('dialects', 'dialects_keys', `Only national and reference; not ${list(extra)}.`, `national و reference فقط، وليس ${list(extra)}.`);
    } else if (!DIALECT_SIDES.every((s) => typeof dialects[s] === 'string' && pyStrip(dialects[s] as string))) {
      push('dialects', 'dialects_both', 'Both dialects are needed: the Who page compares the two.',
        'يلزم تحديد اللهجتين معًا: صفحة «من يتحدث» تقارن بينهما.');
    } else if (pyStrip(dialects.national as string).toLowerCase() === pyStrip(dialects.reference as string).toLowerCase()) {
      push('dialects', 'dialects_differ', 'The two dialects are the same, so there is nothing to compare.',
        'اللهجتان متطابقتان، فلا يوجد ما يُقارَن.');
    }
  }

  const domains = cfg.civic_domains;
  if (present(domains)) {
    if (!Array.isArray(domains)) {
      push('civic_domains', 'civic_domains_list', 'Civic domains is a list of names.', 'المجالات المدنية قائمة بالأسماء.');
    } else if (!domains.every((x) => typeof x === 'string' && pyStrip(x))) {
      push('civic_domains', 'civic_domains_strings', 'A civic domain name cannot be empty.', 'لا يمكن أن يكون اسم المجال المدني فارغًا.');
    } else if (dupes((domains as string[]).map(pyStrip)).length) {
      const d = dupes((domains as string[]).map(pyStrip));
      push('civic_domains', 'civic_domains_unique', `Listed twice: ${list(d)}.`, `مذكور مرتين: ${list(d)}.`);
    }
  }

  const gaps = cfg.taxonomy_gaps;
  if (present(gaps)) {
    if (!isPlainObject(gaps)) {
      push('taxonomy_gaps', 'taxonomy_gaps_object', 'Topics not covered has an English and an Arabic list.',
        'للمواضيع غير المشمولة قائمة بالإنجليزية وأخرى بالعربية.');
    } else if (Object.keys(gaps).some((k) => !(GAP_LANGUAGES as readonly string[]).includes(k))) {
      const extra = Object.keys(gaps).filter((k) => !(GAP_LANGUAGES as readonly string[]).includes(k));
      push('taxonomy_gaps', 'taxonomy_gaps_keys', `Only en and ar; not ${list(extra)}.`, `en و ar فقط، وليس ${list(extra)}.`);
    } else if (!Object.values(gaps).every(isStringList)) {
      push('taxonomy_gaps', 'taxonomy_gaps_lists', 'Each language is a list of short notes.', 'لكل لغة قائمة بملاحظات قصيرة.');
    } else {
      for (const [lang, items] of Object.entries(gaps as Record<string, string[]>)) {
        if (items.length > GAP_MAX_ITEMS) {
          push(`taxonomy_gaps.${lang}`, 'taxonomy_gaps_count', `At most ${GAP_MAX_ITEMS} notes per language; GUS drops the rest.`,
            `${GAP_MAX_ITEMS} ملاحظة كحد أقصى لكل لغة؛ يتجاهل GUS ما زاد عليها.`);
        } else if (items.some((x) => { const n = charCount(pyStrip(x)); return n < 1 || n > GAP_MAX_CHARS; })) {
          push(`taxonomy_gaps.${lang}`, 'taxonomy_gaps_length', `Each note is 1 to ${GAP_MAX_CHARS} characters.`,
            `كل ملاحظة من حرف واحد إلى ${GAP_MAX_CHARS} حرفًا.`);
        }
      }
    }
  }

  const floor = cfg.census_min_count;
  if (present(floor)) {
    if (!isWhole(floor)) {
      push('census_min_count', 'census_min_count_number', 'The census floor is a whole number of people, saved as a number.',
        'الحد الأدنى للتعداد عدد صحيح من الأشخاص، ويُحفظ رقمًا.');
    } else if (floor < 1) {
      push('census_min_count', 'census_min_count_positive', 'The census floor is at least 1. Clear it to have no floor.',
        'الحد الأدنى للتعداد 1 على الأقل. امسحه لإلغاء الحد الأدنى.');
    }
  }

  const pq = cfg.place_quality_raised;
  if (present(pq)) {
    if (!isStringList(pq)) {
      push('place_quality_raised', 'place_quality_list', 'Place quality reasons is a list of reason ids.',
        'أسباب جودة المكان قائمة بمعرّفات الأسباب.');
    } else if (pq.some((x) => !RAISED_IDS.has(x))) {
      const unknown = pq.filter((x) => !RAISED_IDS.has(x));
      push('place_quality_raised', 'place_quality_known', `Not a raised reason GUS knows: ${list(unknown)}.`,
        `ليس من أسباب الشكوى المعروفة لدى GUS: ${list(unknown)}.`);
    } else if (dupes(pq).length) {
      push('place_quality_raised', 'place_quality_unique', `Listed twice: ${list(dupes(pq))}.`, `مذكور مرتين: ${list(dupes(pq))}.`);
    }
  }

  const icons = cfg.topic_icons;
  if (present(icons)) {
    const iconNames = 'Icon names are lower-case letters and digits joined by hyphens, at most '
      + `${ICON_MAX_CHARS} characters (for example heart-pulse)`;
    const iconNamesAr = `أسماء الأيقونات حروف لاتينية صغيرة وأرقام تفصل بينها شرطات، ولا تتجاوز ${ICON_MAX_CHARS} حرفًا (مثل heart-pulse)`;
    const mappedRaw = isPlainObject(icons) ? icons.icons : undefined;
    if (!isPlainObject(icons) || !(mappedRaw === undefined || mappedRaw === null || isPlainObject(mappedRaw))) {
      push('topic_icons', 'topic_icons_object',
        'Topic icons is an object: `icons` maps a topic to an icon name, and `fallback` is one icon name.',
        'أيقونات المواضيع كائن: `icons` يربط كل موضوع باسم أيقونة، و`fallback` اسم أيقونة واحدة.');
    } else if (Object.keys(icons).some((k) => !TOPIC_ICONS_FIELDS.includes(k))) {
      const extra = list(Object.keys(icons).filter((k) => !TOPIC_ICONS_FIELDS.includes(k)));
      push('topic_icons', 'topic_icons_fields', `Topic icons has \`icons\` and \`fallback\`; GUS reads nothing else: ${extra}.`,
        `لأيقونات المواضيع \`icons\` و\`fallback\` فقط؛ ولا يقرأ GUS غيرهما: ${extra}.`);
    } else {
      const mapped: Record<string, unknown> = isPlainObject(mappedRaw) ? mappedRaw : {};
      const badKeys = Object.keys(mapped).filter((k) => !ICON_KEY.test(k));
      const badNames = Object.entries(mapped).filter(([, n]) => !iconNameOk(n)).map(([k]) => k);
      if (badKeys.length) {
        push('topic_icons.icons', 'topic_icons_key',
          `An icon key is * or starts T:, S:, D: or G: and a name: ${list(badKeys)}.`,
          `مفتاح الأيقونة إما * أو يبدأ بـ T: أو S: أو D: أو G: يليه اسم: ${list(badKeys)}.`);
      } else if (badNames.length) {
        push('topic_icons.icons', 'topic_icons_name', `${iconNames}: ${list(badNames)}.`, `${iconNamesAr}: ${list(badNames)}.`);
      }
      if (present(icons.fallback) && !iconNameOk(icons.fallback)) {
        push('topic_icons.fallback', 'topic_icons_name', `${iconNames}.`, `${iconNamesAr}.`);
      }
    }
  }
}

/**
 * The refused values in `after` that `before` (what is stored now) does not
 * already hold. A save is judged on what it adds: a value already stored and
 * left exactly as it was is shown in red but never blocks an unrelated edit.
 * Matched on path, rule, message and the value itself, as the backend matches
 * (introduced_errors): a red value edited into another bad one is refused.
 */
export function introducedErrors(after: unknown, before: unknown): SettingError[] {
  const key = (e: SettingError, flags: unknown) => `${e.path}\u0000${e.rule}\u0000${e.message.en}\u0000${valueAt(flags, e.path)}`;
  const held = new Set(satisfactionErrors(before).map((e) => key(e, before)));
  return satisfactionErrors(after).filter((e) => !held.has(key(e, after)));
}

/** JSON with object keys sorted, so two readings of one value compare equal whatever their key order. */
const canonical = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (isPlainObject(v)) {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v) ?? 'undefined';
};

/** The value at a dot path, as canonical JSON. */
function valueAt(flags: unknown, path: string): string {
  let node: unknown = flags;
  for (const part of path.split('.')) {
    if (!isPlainObject(node) || !(part in node)) return '<absent>';
    node = node[part];
  }
  return canonical(node);
}

/** Every refused value under the Satisfaction tab node of a whole `feature_flags` object. */
export function satisfactionErrors(featureFlags: unknown): SettingError[] {
  let node: unknown = featureFlags;
  for (const part of SATISFACTION_PATH.split('.')) {
    if (!isPlainObject(node)) return [];
    node = node[part];
  }
  if (!isPlainObject(node)) return [];
  const out: SettingError[] = [];
  switchErrors(node, SATISFACTION_PATH, out);
  if (isPlainObject(node.config)) {
    configErrors(node.config, `${SATISFACTION_PATH}.config`, out);
  } else if (node.config !== null && node.config !== undefined) {
    // GUS walks into an object only, so every setting under it would read as its default.
    out.push({ path: `${SATISFACTION_PATH}.config`, rule: 'config_object',
      message: { en: 'The settings are an object of named settings.', ar: 'الإعدادات كائن من إعدادات مسمّاة.' } });
  }
  return out;
}
